// The eyes' own three `GaussianSplat`, written from the GPU lift's readback.
//
// It was a Spark `SplatMesh` over an `ExtSplats` container, and the container is
// what made that a real coupling rather than a loader swap: the lift packed
// Spark's words in the shader and this class copied them into the two arrays
// Spark uploads from. three wants three plain attributes instead — centre,
// covariance, colour — so the lift now writes THOSE (see `eye_lift.wgsl`'s
// `write_three_splat`) and this is three `.set()` calls.
//
// THE LIFT'S MATHS DID NOT MOVE. Only the encoding did: the log-scale/oct-quat
// pair became the covariance three reads, and the opacity — already a VALUE on
// this path, not a logit — became the alpha byte. That matters because
// `scripts/verify_eye_lift.py` pins the lift against the trainer's own numeric
// reference, and a change to the maths would have invalidated the chain that
// makes this branch trustworthy at all (docs/OGS_EYES.md).
//
// ONE MESH OF ITS OWN, deliberately. The eyes are a separate `GaussianSplat`
// from the head, so they sort against it by `renderOrder` rather than per-splat
// depth. That is a real limitation and it is the SAME one this branch has always
// had — it owned its own SplatMesh before. Folding it into the avatar's mesh is
// the fix, and it is a change to the branch's whole present path, not to its
// container.

import * as THREE from 'three'
import { GaussianSplat } from '../render/three/threeSplatDeps.js'

export type EyeSplatMesh = {
  mesh: any
  count: number
  capacity: number
  /** Words per slot across the three blocks the lift writes. */
  readonly wordsPerSlot: number
  updateSplats(words: Uint32Array): void
  dispose(): void
}

/** centre 3 + covariance 6 + colour 1. */
const WORDS_PER_SLOT = 10

export function createEyeSplatMesh(count: number): EyeSplatMesh {
  // No row rounding any more: that existed because Spark FLOORS a partial
  // texture row and silently drops the remainder. three indexes splats
  // directly, so the capacity is the count.
  const capacity = count

  const centre = new Float32Array(capacity * 3)
  const cov = new Float32Array(capacity * 6)
  const colour = new Uint8ClampedArray(capacity * 4)

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.BufferAttribute(centre, 3))
  geometry.setAttribute('covariance', new THREE.BufferAttribute(cov, 6))
  geometry.setAttribute('color', new THREE.BufferAttribute(colour, 4, true))

  const mesh = new GaussianSplat(geometry, { autoSort: true })
  // Never frustum-culled: the eyes sit inside a head, and the bounding sphere is
  // computed from arrays that are all zero until the first lift lands.
  mesh.frustumCulled = false

  // The three blocks are contiguous in the lift's output, so each is one copy.
  const covOffset = capacity * 3
  const colourOffset = capacity * 9

  function updateSplats(words: Uint32Array): void {
    const needed = capacity * WORDS_PER_SLOT
    if (words.length < needed) {
      throw new Error(`eye splats: readback has ${words.length} words, needs ${needed} (capacity ${capacity})`)
    }
    centre.set(new Float32Array(words.buffer, words.byteOffset, capacity * 3))
    cov.set(new Float32Array(words.buffer, words.byteOffset + covOffset * 4, capacity * 6))
    // RGBA8 words straight into the colour bytes: `pack4x8unorm` emits R in the
    // low byte, which is the order the attribute reads them in.
    new Uint32Array(colour.buffer, colour.byteOffset, capacity)
      .set(words.subarray(colourOffset, colourOffset + capacity))

    for (const name of ['position', 'covariance', 'color']) {
      const attr = geometry.getAttribute(name) as THREE.BufferAttribute | undefined
      if (attr) attr.needsUpdate = true
    }
    // The centres moved, so the depth order is stale in a way a camera-direction
    // test cannot see — same rule the avatar's lift follows.
    ;(mesh as unknown as { markSplatsChanged?: () => void }).markSplatsChanged?.()
    geometry.computeBoundingSphere()
  }

  return {
    mesh,
    count,
    capacity,
    wordsPerSlot: WORDS_PER_SLOT,
    updateSplats,
    dispose() {
      mesh.parent?.remove(mesh)
      ;(mesh as unknown as { dispose?: () => void }).dispose?.()
      geometry.dispose()
    },
  }
}
